import { Navigate, Outlet, useLocation } from 'react-router'
import { Loader2 } from 'lucide-react'
import { useAuth } from '@/auth/AuthContext'
import { UpdateBanner } from '@/components/UpdateBanner'

/**
 * Guards nested routes: while the session is being restored it shows a spinner,
 * once resolved it renders the child routes for an authenticated user or
 * redirects to `/login` (preserving the attempted path and query) otherwise.
 */
export function ProtectedRoute() {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="flex min-h-svh items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!isAuthenticated) {
    // The query has to come along. The connect screen's request id lives in
    // `?request=`, and dropping it leaves a signed-in person on a page that
    // can no longer tell which app asked.
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search }}
      />
    )
  }

  /*
   * The banner is mounted here rather than in `AppLayout` on purpose: the
   * survey and boundary screens are deliberately outside that shell, and they
   * are precisely where running an old bundle did damage — a laptop left open
   * across a deploy kept recording work in a format the API had stopped
   * accepting.
   */
  /*
   * A column, not an overlay. The banner was floating at first and at phone
   * width it covered the survey screen's back link AND its sync indicator —
   * the one control that says whether a morning's work has left the device.
   * Taking its own row costs a strip of screen and covers nothing.
   *
   * `min-h-svh` rather than `h-svh` so ordinary pages still grow and scroll;
   * `min-h-0` on the outlet so the full-viewport screens inside it can shrink
   * to what is left instead of overflowing by the banner's height.
   */
  return (
    <div className="flex min-h-svh flex-col">
      <UpdateBanner />
      <div className="flex min-h-0 flex-1 flex-col">
        <Outlet />
      </div>
    </div>
  )
}
